import { Form, FormInstance } from 'antd';
import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import CatalogPriceServiceProviderCell from './CatalogPriceServiceProviderCell';
import CatalogPriceLineProductsCell from './CatalogPriceLineProductsCell';
import PriceValueCell from './PriceValueCell';
import ProductQuantityCell from './ProductQuantityCell';
import {
  CombineCatalogPriceLineZoneServiceProvider,
  SelectedProductsAndSellingPrices,
} from 'hooks/catalog-price-zone-service-provider';
import { PriceType } from 'models';
import { UrbanCenterWithZone } from 'types';
import MargeLineCell from './MargeLineCell';
import ClientOrderPriceCell from './ClientOrderPriceCell';

export type OrderPrestataireLine = CombineCatalogPriceLineZoneServiceProvider & {
  rowSpan?: number;
  isLast?: boolean;
  isFirst?: boolean;
  isDisplay?: boolean;
  isHide?: boolean;
  creationType?: string;
  uuid?: string;
  CatalogPriceLineServiceProvider?: string;
  isNew?: boolean;
};

const EditableContext = React.createContext<FormInstance | null>(null);

/**
 * EditableOrderPrestataireRow component is a wrapper for a table row element
 * that provides a form context to its children using EditableContext.
 *
 * @param props - The HTML attributes for the table row element.
 * @returns A table row component wrapped with a form context provider.
 */
export const EditableOrderPrestataireRow = (props: React.HTMLAttributes<HTMLTableRowElement>) => {
  // Create an instance of Form using Ant Design's Form hook
  const [form] = Form.useForm();

  // Return the table row wrapped in EditableContext.Provider
  return (
    <EditableContext.Provider value={form}>
      <tr {...props} />
    </EditableContext.Provider>
  );
};

interface EditablePrestataireCellProps {
  form: FormInstance;
  title: string;
  editable: boolean;
  children: React.ReactNode;
  inputType: string;
  required: boolean;
  dataIndex: keyof object;
  record: OrderPrestataireLine;
  priceTypes: PriceType[];
  updateDataSource: (data: OrderPrestataireLine, serviceProviderId?: number) => void;
  serviceProviderZoneIds: UrbanCenterWithZone[];
  effectSPIdsLoading: number[];
  setEffectSPIdsLoading: React.Dispatch<React.SetStateAction<number[]>>;
  className?: string;
  selectedProductLines: {
    priceFamilyId?: number;
    productId?: number;
    quantity?: number;
    productNameForClient?: string;
  }[];
  onAssociateProducts?: (
    selectedProducts: { uuid: string; productId: number; productName: string; quantity: number }[],
  ) => void;
  selectedProductsFromOrderClient: SelectedProductsAndSellingPrices[];
}

export type OrderPrestataireCellRef = object;

export const EditableOrderPrestataireCell: React.FC<EditablePrestataireCellProps> = forwardRef<
  OrderPrestataireCellRef,
  EditablePrestataireCellProps
>(
  (
    {
      form,
      editable,
      children,
      dataIndex,
      record,
      priceTypes,
      updateDataSource,
      serviceProviderZoneIds,
      effectSPIdsLoading,
      setEffectSPIdsLoading,
      selectedProductLines,
      onAssociateProducts,
      selectedProductsFromOrderClient,
      ...restProps
    },
    ref,
  ) => {
    const cellDataSource = useMemo(() => {
      return record;
    }, [record]);

    useImperativeHandle(ref, () => ({}));

    /**
     * This function is used to select a price family for a product in the list of products for a service provider.
     * @param args An object containing the product ID and the price family ID to select.
     */
    const onSelectPriceFamily = ({
      uuid,
      productId,
      priceFamilyId,
    }: {
      uuid?: string;
      productId?: number;
      priceFamilyId: number;
    }) => {
      const updatedPriceFamilyId = priceFamilyId;
      const catalogPriceLineProducts = cellDataSource?.products || [];
      const updatedProducts = catalogPriceLineProducts.map((product) => {
        if (product.uuid === uuid && product.productId === productId) {
          const selectedPriceFamily = product?.priceFamilies?.find((priceFamily) => priceFamily.id === priceFamilyId);
          // If the product ID matches the one passed in, update the selected price family ID.
          return {
            ...product,
            selectedPriceFamilyId: updatedPriceFamilyId,
            isManualPrice: false,
            comment: selectedPriceFamily?.comment ?? '',
          };
        }
        // If the product ID does not match, return the product as is.
        return product;
      });
      form.setFieldsValue({
        lineItems: {
          [`${cellDataSource?.serviceProviderId}`]: {
            products: {
              [`${uuid + '-' + productId}`]: {
                priceFamilyId: updatedPriceFamilyId,
              },
            },
          },
        },
      });
      // Update the data source with the new list of products.
      updateDataSource(
        {
          ...cellDataSource,
          products: updatedProducts,
        },
        cellDataSource?.serviceProviderId,
      );
    };

    let childrenNode = <></>;
    /**
     * Render the appropriate cell component based on the dataIndex.
     */
    switch (dataIndex) {
      case 'CatalogPriceLineServiceProvider':
        {
          // Create an object with service provider information
          const catalogPriceSp = {
            serviceProviderId: cellDataSource?.serviceProviderId,
            serviceProviderName: cellDataSource?.serviceProviderName,
            formattedAddress: cellDataSource?.formattedAddress,
          };
          // Get the list of zones for the catalog price line
          const catalogPriceLineZones = cellDataSource?.zones || [];
          // Render the CatalogPriceServiceProviderCell with the service provider information and zones
          childrenNode = (
            <CatalogPriceServiceProviderCell
              value={catalogPriceSp}
              catalogPriceLineZones={catalogPriceLineZones}
              products={cellDataSource?.products || []}
            />
          );
        }
        break;
      case 'CatalogPriceLineProducts':
        {
          // Get the list of products for the catalog price line
          const catalogPriceLineProducts = cellDataSource?.products || [];
          // Render the CatalogPriceLineProductsCell with the products and handlers for price family selection and product removal
          childrenNode = (
            <CatalogPriceLineProductsCell
              form={form}
              value={catalogPriceLineProducts}
              record={cellDataSource}
              onSelectPriceFamily={onSelectPriceFamily}
              priceTypes={priceTypes}
              selectedProductsFromOrderClients={selectedProductsFromOrderClient}
              updateDataSource={updateDataSource}
            />
          );
        }
        break;
      case 'priceValue':
        {
          // Get the list of products for the catalog price line
          const catalogPriceLineProducts = cellDataSource?.products || [];
          // Render the PriceValueCell with the products and price types
          childrenNode = (
            <PriceValueCell
              form={form}
              value={catalogPriceLineProducts}
              record={cellDataSource}
              priceTypes={priceTypes}
              updateDataSource={updateDataSource}
            />
          );
        }
        break;
      case 'clientOrderPrice':
        {
          const productQuantities = (record?.products || []).map((product) => ({
            ...product,
            priceValue: product.priceValue !== undefined ? Number(product.priceValue) : undefined,
          }));
          childrenNode = (
            <ClientOrderPriceCell
              value={productQuantities}
              record={record}
              selectedProductsFromOrderClient={selectedProductsFromOrderClient}
            />
          );
        }
        break;
      case 'marge':
        {
          const productQuantities = cellDataSource?.products || [];
          childrenNode = (
            <MargeLineCell
              form={form}
              value={productQuantities}
              record={cellDataSource}
              priceTypes={priceTypes}
              selectedProductsFromOrderClient={selectedProductsFromOrderClient}
              updateDataSource={updateDataSource}
            />
          );
        }
        break;
      case 'quantity':
        {
          // Get the list of product quantities
          const productQuantities = cellDataSource?.products || [];
          // Render the ProductQuantityCell with the product quantities
          childrenNode = (
            <ProductQuantityCell
              value={productQuantities}
              record={cellDataSource}
              updateDataSource={updateDataSource}
            />
          );
        }
        break;
      default:
        break;
    }
    let childNode = children;
    if (editable) {
      childNode = childrenNode;
    }
    return <td {...restProps}>{childNode}</td>;
  },
);

EditableOrderPrestataireCell.displayName = 'EditableOrderPrestataireCell';
export default EditableOrderPrestataireCell;
